import type { CrremSettings } from "features/energy-analysis-dashboard/model/types";

import { Stack } from "@mui/material";
import { DropdownSingleSelect } from "components/DropdownSingleSelect";
import { CRREM_DISPLAY_METRIC, isCrremDisplayMetric } from "features/energy-analysis-dashboard/model/constants";
import { useTranslation } from "react-i18next";

import type {
  CrremPathwayCorrectionOption,
  CrremPathwayPartitionBy,
  CrremPathwayScale,
  CrremPathwayScenario,
  CrremPathwayViewSettings,
} from "../model/types";

import { CrremPathwaySettingsBadges } from "./CrremPathwaySettingsBadges";
import { CrremPathwaySettingsMenu } from "./CrremPathwaySettingsMenu";

type CrremPathwayChartControlsProps = {
  correctionOptions: CrremPathwayCorrectionOption[];
  disabled?: boolean;
  displayMetric: CrremPathwayViewSettings["displayMetric"];
  onSave: (settings: CrremSettings) => void;
  onScenarioChange: (key: string) => void;
  onViewChange: (change: Partial<CrremPathwayViewSettings>) => void;
  partitionBy: CrremPathwayPartitionBy;
  scale: CrremPathwayScale;
  scenarios: CrremPathwayScenario[];
  selectedScenarioKey?: string;
  settings?: CrremSettings;
  visibleCorrectionIds: string[];
};

export const CrremPathwayChartControls = ({
  correctionOptions,
  disabled,
  displayMetric,
  onSave,
  onScenarioChange,
  onViewChange,
  partitionBy,
  scale,
  scenarios,
  selectedScenarioKey,
  settings,
  visibleCorrectionIds,
}: CrremPathwayChartControlsProps) => {
  const { t } = useTranslation();
  return (
    <Stack className="w-full min-w-0 flex-col items-stretch gap-4">
      <div className="grid w-full min-w-0 grid-cols-12 items-end gap-4">
        <DropdownSingleSelect
          className="col-span-5 min-w-0"
          clearable={false}
          displayOptions={scenarios.map((scenario) => scenario.label)}
          label={t("BuildingProfile.crremPathway.scenario")}
          options={scenarios.map((scenario) => scenario.key)}
          selectedValue={selectedScenarioKey}
          setSelectedValue={(value) => {
            if (typeof value === "string") onScenarioChange(value);
          }}
        />
        <DropdownSingleSelect
          className="col-span-4 min-w-0"
          clearable={false}
          displayOptions={[t("BuildingProfile.crremPathway.emission"), t("BuildingProfile.crremPathway.kwh")]}
          label={t("BuildingProfile.energyAnalysis.settings.metric")}
          options={[CRREM_DISPLAY_METRIC.co2, CRREM_DISPLAY_METRIC.kwh]}
          selectedValue={displayMetric}
          setSelectedValue={(value) => {
            if (!isCrremDisplayMetric(value)) return;
            onViewChange({ displayMetric: value });
          }}
        />
        <div className="col-span-3 min-w-0">
          {settings ? (
            <CrremPathwaySettingsMenu
              correctionOptions={correctionOptions}
              disabled={disabled}
              onSave={onSave}
              onViewChange={onViewChange}
              partitionBy={partitionBy}
              scale={scale}
              settings={settings}
              visibleCorrectionIds={visibleCorrectionIds}
            />
          ) : null}
        </div>
      </div>
      {settings ? <CrremPathwaySettingsBadges scale={scale} settings={settings} /> : null}
    </Stack>
  );
};
